/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTheme } from '../../hooks/useTheme';
import { Wizard, WizardStep, Card, Input, Tile, Switch, TextArea, Select, MenuItem } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useLoader } from '../../hooks/useLoader';
import { AxiosError } from 'axios';
import { Enm, SoftwareRepository, EiapCluster, AutomationFlow } from '../../types/automation-flow.types';
import { useRepositories, useEiapClusters, useEnms, useMoshells, useProductLines } from '../../hooks/useAutomationData';
import { automationFlowService } from '../../services/automationFlowService';
import { getRepositoryTypeInfo, getStatusColor, RepositoryType } from '../../utils/repositoryTypes';

function getStageModel(scope: string): string {
  switch (scope) {
    case 'full':
      return 'Full_Automation';
    case 'onboarding_deployment':
      return 'Onboarding_Deployment';
    case 'onboarding_only':
      return 'Onboarding_Only';
    default:
      return 'Onboarding_Only';
  }
}

function deriveScope(stageModel?: string): string {
  switch (stageModel) {
    case 'Full_Automation':
      return 'full';
    case 'Onboarding_Deployment':
      return 'onboarding_deployment';
    case 'Onboarding_Only':
      return 'onboarding_only';
    default:
      return 'onboarding_only';
  }
}

export default function CrGnbAutomationWizard() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  const templateState = location.state as {
    selectedCard?: { title?: string; wizardTitle?: string; backendTemplate?: string };
    preSelectedProductType?: string;
    artifactTypeRule?: string;
    flowId?: string;
    mode?: 'edit' | 'create';
  } | null;

  const { repositories, fetchRepositories } = useRepositories();
  const { eiapClusters, fetchEiapClusters } = useEiapClusters();
  const { enms, fetchEnms } = useEnms();
  const { moshells, fetchMoshells } = useMoshells();
  const { fetchProductLines } = useProductLines();

  const [originalFlowName, setOriginalFlowName] = useState('');

  const [showMoshell, setShowMoshell] = useState(false);

  const [formData, setFormData] = useState<{
    environmentType: 'verified' | 'unverified' | '';
    scope: 'onboarding_only' | 'onboarding_deployment' | 'full' | '';
    repository: string;
    productLine: string;
    eiapCluster: string;
    enms: string[];
    categoryTools: {
      onboarding: { role: string; toolId: string; endpointSource: string }[];
      deployment: { role: string; toolId: string; endpointSource: string }[];
      kpiEvaluation: { role: string; toolId: string; endpointSource: string }[];
    };
    autoOnboarding: boolean;
    deploymentAutomation: boolean;
    testAutomation: boolean;
    flowName: string;
    flowDescription: string;
  }>({
    environmentType: 'unverified',
    scope: 'full',
    repository: '',
    productLine: '',
    eiapCluster: '',
    enms: [],
    categoryTools: {
      onboarding: [],
      deployment: [],
      kpiEvaluation: []
    },
    autoOnboarding: false,
    deploymentAutomation: false,
    testAutomation: false,
    flowName: '',
    flowDescription: ''
  });

  const isVerified = formData.environmentType === 'verified';
  const showDeploymentStep = templateState?.mode !== 'edit' && (formData.scope === 'onboarding_deployment' || formData.scope === 'full');
  const showKpiStep = false; // later implementation //templateState?.mode !== 'edit' && (formData.scope === 'onboarding_deployment' || formData.scope === 'full');
  const showTestingStep = templateState?.mode !== 'edit' && formData.scope === 'full';

  const validateFlowData = (
    flow: AutomationFlow,
    fetchedRepos: SoftwareRepository[],
    fetchedClusters: EiapCluster[],
    fetchedEnms: Enm[]
  ) => {
    const repoId = flow.configuration?.repository;
    const repoExists = repoId === 'swgw' || fetchedRepos.some((r) => r.id === repoId);
    if (!repoExists && repoId) {
      addNotifications([
        {
          title: 'Warning - Software Repository',
          icon: 'icon-triangle-warning',
          description: 'The previously configured software repository is no longer available. Please select a new one.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }
    const eiapClusterId = flow.eiapClusterId || '';
    const eiapExists = !eiapClusterId || fetchedClusters.some((c) => c.id === eiapClusterId);
    if (!eiapExists) {
      addNotifications([
        {
          title: 'Warning - EIAP Cluster',
          icon: 'icon-triangle-warning',
          description: 'The previously configured EIAP Cluster is no longer available. Please select a new one.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }
    const savedEnmIds = flow.enms?.map((e: Enm) => e.id) || [];
    const validEnmIds = savedEnmIds.filter((id: string) => fetchedEnms.some((e) => e.id === id));
    if (validEnmIds.length < savedEnmIds.length && savedEnmIds.length > 0) {
      addNotifications([
        {
          title: 'Warning - ENMs',
          icon: 'icon-triangle-warning',
          description: 'One or more previously configured ENMs are no longer available. Please review your selection.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }
    return {
      repository: repoExists ? repoId : '',
      eiapCluster: eiapExists ? eiapClusterId : '',
      enms: validEnmIds
    };
  };

  const loadAutomationFlow = async (
    flowId: string,
    fetchedRepos: SoftwareRepository[],
    fetchedClusters: EiapCluster[],
    fetchedEnms: Enm[]
  ) => {
    try {
      const resp = await automationFlowService.getById(flowId);
      const flow = resp.data;
      const validated = validateFlowData(flow, fetchedRepos, fetchedClusters, fetchedEnms);
      setFormData((prev) => ({
        ...prev,
        flowName: flow.name || '',
        flowDescription: flow.description || '',
        repository: validated.repository || '',
        productLine: flow.productLineId || '',
        eiapCluster: validated.eiapCluster || '',
        enms: validated.enms,
        environmentType: flow.configuration?.environmentType || (flow.artifactTypeRule === 'verified' ? 'verified' : 'unverified'),
        scope: flow.configuration?.scope || deriveScope(flow.stageModel || flow.template?.name),
        autoOnboarding: flow.configuration?.autoOnboarding ?? true,
        deploymentAutomation: flow.configuration?.deploymentAutomation ?? false,
        testAutomation: flow.configuration?.testAutomation ?? false,
        categoryTools: flow.configuration?.categoryTools || { onboarding: [], deployment: [], kpiEvaluation: [] }
      }));
      setOriginalFlowName(flow.name || '');
    } catch (error) {
      addNotifications([
        {
          title: 'Error - Automation Flow',
          icon: 'icon-triangle-warning',
          description:
            error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching automation flow.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      showLoader();
      try {
        const [reposResult, clustersResult, enmsResult] = await Promise.all([
          fetchRepositories().catch(() => []),
          fetchEiapClusters().catch(() => []),
          fetchEnms().catch(() => []),
          fetchMoshells().catch(() => [])
        ]);
        await fetchProductLines('CR gNB');

        if (!reposResult || reposResult.length === 0) {
          setFormData((prev) => ({ ...prev, repository: 'swgw' }));
        }
        if (clustersResult && clustersResult.length === 1) {
          setFormData((prev) => ({ ...prev, eiapCluster: clustersResult[0].id, enms: [] }));
        }

        if (templateState?.mode === 'edit' && templateState?.flowId) {
          await loadAutomationFlow(templateState.flowId, reposResult || [], clustersResult || [], enmsResult || []);
        }
      } finally {
        setTimeout(() => hideLoader(), 500);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedEiapCluster = eiapClusters.find((c) => c.id === formData.eiapCluster);
  const availableEnms = selectedEiapCluster ? enms.filter((enm) => enm.linkedEiapClusters.includes(selectedEiapCluster.id)) : enms;

  useEffect(() => {
    if (availableEnms.length === 1 && formData.enms.length === 0) {
      setFormData((prev) => ({ ...prev, enms: [availableEnms[0].id] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableEnms.length]);

  const setTool = (
    category: 'onboarding' | 'deployment' | 'kpiEvaluation',
    role: string,
    toolId: string,
    selected: boolean,
    endpointSource: string
  ) => {
    const tools = formData.categoryTools[category].filter((t) => t.role !== role);
    if (selected) tools.push({ role, toolId, endpointSource });
    setFormData({ ...formData, categoryTools: { ...formData.categoryTools, [category]: tools } });
  };

  const hasTool = (category: 'onboarding' | 'deployment' | 'kpiEvaluation', role: string, toolId: string, endpointSource: string) =>
    formData.categoryTools[category].some((t) => t.role === role && t.toolId === toolId && t.endpointSource === endpointSource);

  const handleCancel = () => navigate('/automation-flows/new');

  const handleFinish = async () => {
    if (!formData.flowName.trim()) {
      addNotifications([
        {
          title: 'Validation Error',
          icon: 'icon-triangle-warning',
          description: 'Flow name is required.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
      return;
    }
    if (!formData.eiapCluster) {
      addNotifications([
        {
          title: 'Validation Error',
          icon: 'icon-triangle-warning',
          description: 'Please select a Target Environment (EIAP Cluster).',
          color: 'red',
          timestamp: new Date()
        }
      ]);
      return;
    }
    if (formData.enms.length === 0) {
      addNotifications([
        {
          title: 'Validation Error',
          icon: 'icon-triangle-warning',
          description: 'Please select at least one ENM.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
      return;
    }
    showLoader();
    try {
      const configuration = {
        repository: formData.repository,
        repositoryType: formData.repository === 'swgw' ? 'swgw' : formData.repository === 'internal-oci' ? 'internal' : 'external',
        environmentType: formData.environmentType,
        scope: formData.scope,
        autoOnboarding: formData.autoOnboarding,
        deploymentAutomation: formData.deploymentAutomation,
        testAutomation: formData.testAutomation,
        categoryTools: formData.categoryTools
      };

      if (templateState?.mode === 'edit' && templateState?.flowId) {
        await automationFlowService.update(templateState.flowId, {
          name: formData.flowName.trim(),
          description: formData.flowDescription.trim(),
          eiapClusterId: formData.eiapCluster || null,
          enmIds: formData.enms,
          configuration
        });
        addNotifications([
          {
            title: 'Success',
            icon: 'icon-check',
            description: 'Cloud RAN Automation Flow updated successfully!',
            color: 'green',
            timestamp: new Date()
          }
        ]);
      } else {
        await automationFlowService.create({
          name: formData.flowName.trim(),
          description: formData.flowDescription.trim(),
          cardName: 'Cloud Ran gNB Workflow',
          productType: 'gNB',
          stageModel: getStageModel(formData.scope),
          artifactTypeRule: formData.environmentType === 'verified' ? 'verified' : 'unverified',
          eiapClusterId: formData.eiapCluster || null,
          enmIds: formData.enms,
          configuration,
          status: 'active'
        });
        addNotifications([
          {
            title: 'Success',
            icon: 'icon-check',
            description: 'Cloud RAN Automation Flow created successfully!',
            color: 'green',
            timestamp: new Date()
          }
        ]);
      }
      navigate('/automation-flows');
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.message || error.message : 'Failed to save automation flow.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    } finally {
      setTimeout(() => hideLoader(), 500);
    }
  };

  const validateRLCAHC = () => {
    const showingMoshell = !showMoshell;
    setShowMoshell(showingMoshell);
    if (!showingMoshell) {
      setTool('deployment', 'preHealthCheck', '', false, 'moshell');
    }
  };

  const baseTitle = templateState?.selectedCard?.wizardTitle || templateState?.selectedCard?.title || 'Configure CR gNodeB Automation';
  const wizardTitle = templateState?.mode === 'edit' && originalFlowName ? `${baseTitle}: ${originalFlowName}` : baseTitle;

  return (
    <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
      <ReactEDSWrapper component={Tile} title={wizardTitle} id="configure-automation-flow-tile">
        <p style={{ marginBottom: '16px', color: '#8b8b8b' }}>Follow the steps to configure a new automation workflow.</p>
        <ReactEDSWrapper
          component={Wizard}
          onCancel={handleCancel}
          onFinish={handleFinish}
          nextBtnLabel="Next"
          previousBtnLabel="Previous"
          cancelBtnLabel="Cancel"
          finishBtnLabel={templateState?.mode === 'edit' ? 'Update Automation Flow' : 'Create Automation Flow'}
        >
          {/* Step 1: Initial Information */}
          <ReactEDSWrapper
            component={WizardStep}
            stepTitle="Initial Information"
            valid={!!formData.flowName && !!formData.environmentType && !!formData.scope}
          >
            <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>Provide the initial details to start setting up your workflow.</p>
            <div className="row">
              <div className="column sm-12">
                <ReactEDSWrapper
                  component={Input}
                  required
                  label="Name"
                  placeholder="Automation Flow 1"
                  value={formData.flowName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, flowName: e.target.value })}
                />
              </div>
              <div className="column sm-12" style={{ marginTop: '16px' }}>
                <ReactEDSWrapper
                  component={TextArea}
                  label="Description"
                  placeholder="Automation Flow Description"
                  value={formData.flowDescription}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, flowDescription: e.target.value })}
                  maxLength={150}
                  style={{ width: '25%' }}
                />
              </div>
            </div>

            {/* Artifact Readiness */}
            <div style={{ marginTop: '24px' }}>
              <h4 style={{ marginBottom: '4px' }}>Artifact Readiness</h4>
              <p style={{ color: '#8b8b8b', marginBottom: '12px' }}>Select the current readiness state of your artifact.</p>
              <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                <div style={{ marginBottom: '8px' }}>
                  <input
                    id="gnb-env-unverified"
                    name="gnbEnvironmentType"
                    type="radio"
                    checked={formData.environmentType === 'unverified'}
                    onChange={() => setFormData({ ...formData, environmentType: 'unverified', repository: '' })}
                  />
                  <label htmlFor="gnb-env-unverified">Requires Validation</label>
                  <p style={{ color: '#8b8b8b', fontSize: '12px', marginLeft: '24px', marginTop: '2px' }}>
                    New artifact that has not yet been validated through deployment and testing.
                  </p>
                </div>
                <div>
                  <input
                    id="gnb-env-verified"
                    name="gnbEnvironmentType"
                    type="radio"
                    checked={formData.environmentType === 'verified'}
                    onChange={() =>
                      setFormData({
                        ...formData,
                        environmentType: 'verified',
                        repository: 'internal-oci',
                        ...(formData.scope === 'full' && { scope: 'onboarding_deployment' as const })
                      })
                    }
                  />
                  <label htmlFor="gnb-env-verified">Ready for Release</label>
                  <p style={{ color: '#8b8b8b', fontSize: '12px', marginLeft: '24px', marginTop: '2px' }}>
                    Artifact has already completed validation through deployment and testing.
                  </p>
                </div>
              </fieldset>
            </div>

            {/* Automation Scope */}
            <div style={{ marginTop: '24px' }}>
              <h4 style={{ marginBottom: '4px' }}>Automation Scope</h4>
              <p style={{ color: '#8b8b8b', marginBottom: '12px' }}>Choose how far the automation pipeline should execute.</p>
              <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                <div style={{ marginBottom: '8px' }}>
                  <input
                    id="gnb-scope-onboarding"
                    name="gnbScope"
                    type="radio"
                    checked={formData.scope === 'onboarding_only'}
                    onChange={() =>
                      setFormData({ ...formData, scope: 'onboarding_only', deploymentAutomation: false, testAutomation: false })
                    }
                  />
                  <label htmlFor="gnb-scope-onboarding">Onboarding Only</label>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <input
                    id="gnb-scope-deployment"
                    name="gnbScope"
                    type="radio"
                    checked={formData.scope === 'onboarding_deployment'}
                    onChange={() => setFormData({ ...formData, scope: 'onboarding_deployment', testAutomation: false })}
                  />
                  <label htmlFor="gnb-scope-deployment">Onboarding + Deployment</label>
                </div>
                {!isVerified && (
                  <div>
                    <input
                      id="gnb-scope-full"
                      name="gnbScope"
                      type="radio"
                      checked={formData.scope === 'full'}
                      onChange={() => setFormData({ ...formData, scope: 'full' })}
                    />
                    <label htmlFor="gnb-scope-full">Full Automation (+ Testing)</label>
                  </div>
                )}
              </fieldset>
            </div>
          </ReactEDSWrapper>

          {/* Step 2: SW Repository Connection */}
          <ReactEDSWrapper component={WizardStep} stepTitle="SW Repository Connection" valid={!!formData.repository}>
            <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>Select the software repository connection for this workflow.</p>
            {isVerified ? (
              <>
                <div className="row" style={{ marginBottom: '24px' }}>
                  <div className="column sm-12">
                    <h5>Internal OCI Registry</h5>
                  </div>
                  <div className="column sm-12 md-6 lg-4">
                    <ReactEDSWrapper
                      component={Card}
                      title="Internal OCI Registry"
                      selected={formData.repository === 'internal-oci'}
                      onChange={(selected: any) => setFormData({ ...formData, repository: selected ? 'internal-oci' : '' })}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                        <span>Pre-verified production artifacts</span>
                      </div>
                      <p style={{ fontSize: '12px', color: '#6a6a6a' }}>
                        Use the internal OCI registry for verified artifacts that are already registered.
                      </p>
                    </ReactEDSWrapper>
                  </div>
                </div>
                <div className="row">
                  <div className="column sm-12">
                    <h5>External Repositories</h5>
                  </div>
                  {repositories.length === 0 ? (
                    <div className="column sm-12">
                      <p style={{ color: '#6a6a6a' }}>
                        No external repositories configured. <Link to="/sw-repositories/external-repositories">Configure one</Link> if
                        needed.
                      </p>
                    </div>
                  ) : (
                    repositories.map((repo) => (
                      <div key={repo.id} className="column sm-12 md-6 lg-4">
                        <ReactEDSWrapper
                          component={Card}
                          title={repo.name}
                          subtitle={getRepositoryTypeInfo(repo.type as RepositoryType)?.name}
                          selected={formData.repository === repo.id}
                          onChange={(selected: any) => setFormData({ ...formData, repository: selected ? repo.id : '' })}
                        >
                          <span>
                            <i className="icon icon-link" style={{ marginRight: '8px' }} />
                            {repo.url}
                          </span>
                          <br />
                          <span>
                            <i className="icon icon-avatar" style={{ marginRight: '8px' }} />
                            {repo.username}
                          </span>
                          <br />
                          <span>
                            <i
                              className="icon icon-alarm-level4"
                              style={{ fontSize: '8px', color: getStatusColor(repo.status), marginRight: '6px' }}
                            />
                            <span style={{ fontSize: '11px', textTransform: 'capitalize', color: '#6a6a6a' }}>{repo.status}</span>
                          </span>
                        </ReactEDSWrapper>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="row" style={{ marginBottom: '24px' }}>
                  <div className="column sm-12">
                    <h5>Ericsson Software Gateway</h5>
                  </div>
                  <div className="column sm-12 md-6 lg-4">
                    <ReactEDSWrapper
                      component={Card}
                      title="Ericsson Software Gateway"
                      selected={formData.repository === 'swgw'}
                      onChange={(selected: any) => setFormData({ ...formData, repository: selected ? 'swgw' : '' })}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                        <span>Official Ericsson Software Distribution</span>
                      </div>
                      <p style={{ fontSize: '12px', color: '#6a6a6a' }}>
                        Connect to Ericsson's centralized software gateway for official product releases
                      </p>
                    </ReactEDSWrapper>
                  </div>
                </div>
                <div className="row">
                  <div className="column sm-12">
                    <h5>External Repositories</h5>
                  </div>
                  {repositories.map((repo) => (
                    <div key={repo.id} className="column sm-12 md-6 lg-4">
                      <ReactEDSWrapper
                        component={Card}
                        title={repo.name}
                        subtitle={getRepositoryTypeInfo(repo.type as RepositoryType)?.name}
                        selected={formData.repository === repo.id}
                        onChange={(selected: any) => setFormData({ ...formData, repository: selected ? repo.id : '' })}
                      >
                        <span>
                          <i className="icon icon-link" style={{ marginRight: '8px' }} />
                          {repo.url}
                        </span>
                        <br />
                        <span>
                          <i className="icon icon-avatar" style={{ marginRight: '8px' }} />
                          {repo.username}
                        </span>
                        <br />
                        <span>
                          <i
                            className="icon icon-alarm-level4"
                            style={{ fontSize: '8px', color: getStatusColor(repo.status), marginRight: '6px' }}
                          />
                          <span style={{ fontSize: '11px', textTransform: 'capitalize', color: '#6a6a6a' }}>{repo.status}</span>
                        </span>
                      </ReactEDSWrapper>
                    </div>
                  ))}
                </div>
              </>
            )}
          </ReactEDSWrapper>

          {/* Step 3: Onboarding */}
          <ReactEDSWrapper
            component={WizardStep}
            stepTitle="Onboarding"
            valid={!!formData.eiapCluster && formData.enms.length > 0 && formData.categoryTools.onboarding.length > 0}
          >
            <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>
              Configure your environment, set endpoints, and choose how onboarding will run.
            </p>

            <h4>Onboarding Trigger</h4>
            <p style={{ color: '#8b8b8b', marginBottom: '8px' }}>
              Choose whether the onboarding should start manually or automatically. <br></br>Auto-Onboarding On automatically triggers the
              onboarding stage.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <label style={{ fontWeight: '500' }}>Auto-Onboarding:</label>
              <ReactEDSWrapper
                component={Switch}
                checked={formData.autoOnboarding}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, autoOnboarding: e.target.checked })}
                enabledLabel="On"
                disabledLabel="Off"
              />
            </div>

            <h4>
              Select Target Environment <span style={{ color: '#0069C2' }}>*</span>
            </h4>
            <div style={{ marginBottom: '24px' }}>
              <ReactEDSWrapper
                component={Select}
                label="Select environment"
                value={formData.eiapCluster}
                onChange={(event: any) => {
                  const raw = event?.target?.value ?? event;
                  const value = Array.isArray(raw) ? raw[0] : raw;
                  setFormData({ ...formData, eiapCluster: value, enms: [] });
                  const cluster = eiapClusters.find((c) => c.id === value);
                  const hasEnm = ((cluster as any)?.endpoints || []).some((ep: any) => ep.targetEnvToolType?.name === 'ENM');
                  if (!hasEnm && value) {
                    addNotifications([
                      {
                        title: 'Warning - No ENMs',
                        icon: 'icon-triangle-warning',
                        description: 'This cluster has no ENMs linked. Please link ENMs in Endpoints before proceeding.',
                        color: 'yellow',
                        timestamp: new Date()
                      }
                    ]);
                  }
                }}
              >
                {eiapClusters.map((cluster) => (
                  <ReactEDSWrapper
                    component={MenuItem}
                    key={cluster.id}
                    value={cluster.id}
                  >{`${cluster.name} - ${(cluster as any).category}`}</ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
            </div>

            {/* ENM Selection (shows after EIAP cluster selected) */}
            {formData.eiapCluster && (
              <div className="row" style={{ marginBottom: '24px' }}>
                <div className="column sm-12">
                  <h4>
                    Select ENMs <span style={{ color: '#0069C2' }}>*</span>
                  </h4>
                  <p style={{ color: '#8b8b8b', marginBottom: '8px' }}>
                    Select the live ENM connection(s) linked to this environment where SW onboarding will execute.
                  </p>
                </div>
                {availableEnms.length === 0 ? (
                  <div className="column sm-12">
                    <p style={{ color: '#6a6a6a' }}>
                      No ENMs linked to this cluster. <Link to="/integrations/enms">Link ENMs</Link> first.
                    </p>
                  </div>
                ) : (
                  availableEnms.map((enm) => (
                    <div key={enm.id} className="column sm-12 md-6 lg-4">
                      <ReactEDSWrapper
                        component={Card}
                        title={enm.name}
                        selected={formData.enms.includes(enm.id)}
                        onChange={(selected: any) => {
                          const updatedEnms = selected ? [...formData.enms, enm.id] : formData.enms.filter((e) => e !== enm.id);
                          setFormData({ ...formData, enms: updatedEnms });
                        }}
                      >
                        <span>
                          <i className="icon icon-link" style={{ marginRight: '8px' }} />
                          {enm.url}
                        </span>
                        {enm.description && <p style={{ fontSize: '12px', color: '#6a6a6a', marginTop: '4px' }}>{enm.description}</p>}
                      </ReactEDSWrapper>
                    </div>
                  ))
                )}
              </div>
            )}

            <h4>
              Select Onboarding tool<span style={{ color: '#0069C2' }}>*</span>
            </h4>
            <h5>RLO rApp</h5>
            <div className="row" style={{ marginBottom: '24px' }}>
              <div className="column sm-12 md-6 lg-4">
                <ReactEDSWrapper
                  component={Card}
                  title="RLO rApp Connection"
                  selected={hasTool('onboarding', 'onboarding', 'rloRapp', 'other')}
                  onChange={(selected: any) => setTool('onboarding', 'onboarding', 'rloRapp', selected, 'other')}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                    <span>Ericsson's RAN Lifecycle Orchestrator (RLO).</span>
                  </div>
                </ReactEDSWrapper>
              </div>
            </div>

            <h5>External Tools</h5>
            <div className="row">
              <div className="lg 12" style={{ color: '#8b8b8b' }}>
                Coming Soon...
              </div>
              {/* For later impletemation of External Tools
              {repositories.length === 0 ? (
                <div className="column sm-12">
                  <p style={{ color: '#6a6a6a' }}>
                    Please configure an <Link to="/sw-repositories/external-repositories">External Repository</Link> if needed as an
                    onboarding tool.
                  </p>
                </div>
              ) : (
                repositories.map((repo) => (
                  <div key={repo.id} className="column sm-12 md-6 lg-4">
                    <ReactEDSWrapper
                      component={Card}
                      title={repo.name}
                      subtitle="JFrog Artifactory"
                      selected={hasTool('onboarding', 'onboarding', repo.id, 'other')}
                      onChange={(selected: any) => setTool('onboarding', 'onboarding', repo.id, selected, 'repository')}
                    >
                      <span>
                        <i className="icon icon-link" style={{ marginRight: '8px' }} />
                        {repo.url}
                      </span>
                      <br />
                      <span>
                        <i className="icon icon-avatar" style={{ marginRight: '8px' }} />
                        {repo.username}
                      </span>
                      <br />
                      <span>
                        <i
                          className="icon icon-alarm-level4"
                          style={{ fontSize: '8px', color: getStatusColor(repo.status), marginRight: '6px' }}
                        />
                        <span style={{ fontSize: '11px', textTransform: 'capitalize' }}>{repo.status}</span>
                      </span>
                    </ReactEDSWrapper>
                  </div>
                ))
              )} */}
            </div>
          </ReactEDSWrapper>

          {/* Steps 4-6: Deployment, KPI, Testing (scope-dependent) */}
          {[
            showDeploymentStep ? (
              <ReactEDSWrapper
                key="deployment"
                component={WizardStep}
                stepTitle="Deployment"
                valid={
                  formData.categoryTools.deployment.some((t) => t.role === 'preHealthCheck') &&
                  formData.categoryTools.deployment.some((t) => t.role === 'deployment')
                }
              >
                <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>Control how and when automated deployment is triggered.</p>

                <h4>
                  Deployment Trigger<span style={{ color: '#0069C2' }}>*</span>
                </h4>
                <p style={{ color: '#8b8b8b', marginBottom: '8px' }}>
                  Choose whether the deployment should start manually or automatically.
                  <br></br>Auto-Deploy On automatically triggers the deployment stage.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                  <label style={{ fontWeight: '500' }}>Auto-Deploy:</label>
                  <ReactEDSWrapper
                    component={Switch}
                    checked={formData.deploymentAutomation}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, deploymentAutomation: e.target.checked })
                    }
                    enabledLabel="On"
                    disabledLabel="Off"
                  />
                </div>

                <h4>
                  Select Health Check Tool<span style={{ color: '#0069C2' }}>*</span>
                </h4>
                <div className="row" style={{ marginBottom: '24px' }}>
                  {/* For later RHC implementation
                  <div className="column sm-12 md-6 lg-4">
                    <ReactEDSWrapper
                      component={Card}
                      title="RHC Healthcheck"
                      selected={hasTool('deployment', 'preHealthCheck', 'rhc', 'other')}
                      onChange={(selected: any) => setTool('deployment', 'preHealthCheck', 'rhc', selected, 'other')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                        <span>Ericsson's RHC (RAN Health Check) rApp.</span>
                      </div>
                    </ReactEDSWrapper>
                  </div> */}
                  <div className="column sm-12 md-6 lg-4">
                    <ReactEDSWrapper component={Card} title="RLCA Healthcheck" onChange={() => validateRLCAHC()}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                        <span>In RAN Life Cycle Automation Platform Health Check using Moshell.</span>
                      </div>
                    </ReactEDSWrapper>
                  </div>
                </div>

                {showMoshell && (
                  <>
                    <h5>Moshell Endpoints</h5>
                    <div className="row" style={{ marginBottom: '24px' }}>
                      {moshells.filter((m) => m.category === (selectedEiapCluster as any)?.category).length === 0 ? (
                        <div className="column sm-12">
                          <p style={{ color: '#6a6a6a' }}>
                            Please configure <Link to="/integrations/moshell">Moshell Endpoints</Link> if needed as a pre-health check tool.
                          </p>
                        </div>
                      ) : (
                        moshells
                          .filter((m) => m.category === (selectedEiapCluster as any)?.category)
                          .map((moshell) => (
                            <div key={moshell.id} className="column sm-12 md-6 lg-4">
                              <ReactEDSWrapper
                                component={Card}
                                title={moshell.name}
                                selected={hasTool('deployment', 'preHealthCheck', moshell.id!, 'moshell')}
                                onChange={(selected: any) => setTool('deployment', 'preHealthCheck', moshell.id!, selected, 'moshell')}
                              >
                                <span>
                                  <i className="icon icon-link" style={{ marginRight: '8px' }} />
                                  {moshell.endpoint}
                                </span>
                              </ReactEDSWrapper>
                            </div>
                          ))
                      )}
                    </div>
                  </>
                )}

                <h5>External Tools</h5>
                <div className="row" style={{ marginBottom: '24px' }}>
                  <div className="lg 12" style={{ color: '#8b8b8b' }}>
                    Coming Soon...
                  </div>
                  {/* For later implementation of External Tools
                  {repositories.length === 0 ? (
                    <div className="column sm-12">
                      <p style={{ color: '#6a6a6a' }}>
                        Please configure an <Link to="/sw-repositories/external-repositories">External Repository</Link> if needed as a
                        pre-health check tool.
                      </p>
                    </div>
                  ) : (
                    repositories.map((repo) => (
                      <div key={repo.id} className="column sm-12 md-6 lg-4" style={{ marginBottom: '16px' }}>
                        <ReactEDSWrapper
                          component={Card}
                          title={repo.name}
                          subtitle="JFrog Artifactory"
                          selected={hasTool('deployment', 'preHealthCheck', repo.id, 'other')}
                          onChange={(selected: any) => setTool('deployment', 'preHealthCheck', repo.id, selected, 'repository')}
                        >
                          <span>
                            <i className="icon icon-link" style={{ marginRight: '8px' }} />
                            {repo.url}
                          </span>
                          <br />
                          <span>
                            <i className="icon icon-avatar" style={{ marginRight: '8px' }} />
                            {repo.username}
                          </span>
                          <br />
                          <span>
                            <i
                              className="icon icon-alarm-level4"
                              style={{ fontSize: '8px', color: getStatusColor(repo.status), marginRight: '6px' }}
                            />
                            <span style={{ fontSize: '11px', textTransform: 'capitalize' }}>{repo.status}</span>
                          </span>
                        </ReactEDSWrapper>
                      </div>
                    ))
                  )} */}
                </div>

                <h4>
                  Select Deployment Tool<span style={{ color: '#0069C2' }}>*</span>
                </h4>
                <div className="row" style={{ marginBottom: '24px' }}>
                  <div className="column sm-12 md-6 lg-4">
                    <ReactEDSWrapper
                      component={Card}
                      title="RLO rApp"
                      selected={hasTool('deployment', 'deployment', 'rloRapp', 'other')}
                      onChange={(selected: any) => setTool('deployment', 'deployment', 'rloRapp', selected, 'other')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                        <span>Ericssons RAN Lifecycle Orchestrator (RLO).</span>
                      </div>
                    </ReactEDSWrapper>
                  </div>
                </div>
                <h5>External Tools</h5>
                <div className="row">
                  <div className="lg 12" style={{ color: '#8b8b8b' }}>
                    Coming Soon...
                  </div>
                  {/* For later implementation of External Tools
                  {repositories.length === 0 ? (
                    <div className="column sm-12">
                      <p style={{ color: '#6a6a6a' }}>
                        Please configure an <Link to="/sw-repositories/external-repositories">External Repository</Link> if needed as a
                        deployment tool.
                      </p>
                    </div>
                  ) : (
                    repositories.map((repo) => (
                      <div key={repo.id} className="column sm-12 md-6 lg-4" style={{ marginBottom: '16px' }}>
                        <ReactEDSWrapper
                          component={Card}
                          title={repo.name}
                          subtitle="JFrog Artifactory"
                          selected={hasTool('deployment', 'deployment', repo.id, 'repository')}
                          onChange={(selected: any) => setTool('deployment', 'deployment', repo.id, selected, 'repository')}
                        >
                          <span>
                            <i className="icon icon-link" style={{ marginRight: '8px' }} />
                            {repo.url}
                          </span>
                          <br />
                          <span>
                            <i className="icon icon-avatar" style={{ marginRight: '8px' }} />
                            {repo.username}
                          </span>
                          <br />
                          <span>
                            <i
                              className="icon icon-alarm-level4"
                              style={{ fontSize: '8px', color: getStatusColor(repo.status), marginRight: '6px' }}
                            />
                            <span style={{ fontSize: '11px', textTransform: 'capitalize' }}>{repo.status}</span>
                          </span>
                        </ReactEDSWrapper>
                      </div>
                    ))
                  )} */}
                </div>
              </ReactEDSWrapper>
            ) : null,
            showKpiStep ? (
              <ReactEDSWrapper
                key="kpi"
                component={WizardStep}
                stepTitle="KPI Evaluation"
                valid={formData.categoryTools.kpiEvaluation.length > 0}
              >
                <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>Configure KPI evaluation tools.</p>

                <h4>External Tools</h4>
                <div className="row">
                  {repositories.length === 0 ? (
                    <div className="column sm-12">
                      <p style={{ color: '#6a6a6a' }}>
                        Please configure an <Link to="/sw-repositories/external-repositories">External Repository</Link> if needed as a KPI
                        Evaluation tool.
                      </p>
                    </div>
                  ) : (
                    repositories.map((repo) => (
                      <div key={repo.id} className="column sm-12 md-6 lg-4" style={{ marginBottom: '16px' }}>
                        <ReactEDSWrapper
                          component={Card}
                          title={repo.name}
                          subtitle="JFrog Artifactory"
                          selected={hasTool('kpiEvaluation', 'kpiEvaluation', repo.id, 'other')}
                          onChange={(selected: any) => setTool('kpiEvaluation', 'kpiEvaluation', repo.id, selected, 'repository')}
                        >
                          <span>
                            <i className="icon icon-link" style={{ marginRight: '8px' }} />
                            {repo.url}
                          </span>
                          <br />
                          <span>
                            <i className="icon icon-avatar" style={{ marginRight: '8px' }} />
                            {repo.username}
                          </span>
                          <br />
                          <span>
                            <i
                              className="icon icon-alarm-level4"
                              style={{ fontSize: '8px', color: getStatusColor(repo.status), marginRight: '6px' }}
                            />
                            <span style={{ fontSize: '11px', textTransform: 'capitalize' }}>{repo.status}</span>
                          </span>
                        </ReactEDSWrapper>
                      </div>
                    ))
                  )}
                </div>
              </ReactEDSWrapper>
            ) : null,
            showTestingStep ? (
              <ReactEDSWrapper key="testing" component={WizardStep} stepTitle="Testing">
                <h4>
                  Configure Test Trigger <span style={{ color: '#0069C2' }}> *</span>
                </h4>
                <p style={{ color: '#8b8b8b', marginBottom: '8px' }}>Choose whether testing should start manually or automatically.</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <label style={{ fontWeight: '500' }}>Auto-Test:</label>
                  <ReactEDSWrapper
                    component={Switch}
                    checked={formData.testAutomation}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, testAutomation: e.target.checked })}
                    enabledLabel="On"
                    disabledLabel="Off"
                  />
                </div>
              </ReactEDSWrapper>
            ) : null
          ].filter(Boolean)}

          {/* Step 7: Review & Create */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Review & Create" valid={!!formData.flowName}>
            <p style={{ color: '#8b8b8b', marginBottom: '16px' }}>Review your configuration before creating.</p>
            <ReactEDSWrapper component={Card} title="Configuration Summary">
              <div style={{ display: 'grid', gap: '12px' }}>
                <div>
                  <strong>Name:</strong> {formData.flowName || 'Not set'}
                </div>
                <div>
                  <strong>Description:</strong> {formData.flowDescription || 'Not set'}
                </div>
                <div>
                  <strong>Artifact Readiness:</strong>{' '}
                  {formData.environmentType === 'verified' ? 'Ready for Deployment' : 'Requires Validation'}
                </div>
                <div>
                  <strong>Automation Scope:</strong>{' '}
                  {formData.scope === 'full'
                    ? 'Full Automation'
                    : formData.scope === 'onboarding_deployment'
                      ? 'Onboarding + Deployment'
                      : 'Onboarding Only'}
                </div>
                <div>
                  <strong>SW Repository:</strong>{' '}
                  {formData.repository === 'swgw'
                    ? 'Ericsson Software Gateway'
                    : formData.repository === 'internal-oci'
                      ? 'Internal OCI Registry'
                      : repositories.find((r) => r.id === formData.repository)?.name || 'Not selected'}
                </div>
                <div>
                  <strong>Target Environment:</strong> {eiapClusters.find((c) => c.id === formData.eiapCluster)?.name || 'Not selected'}
                </div>
                <div>
                  <strong>ENMs:</strong>{' '}
                  {formData.enms.length > 0
                    ? formData.enms
                        .map((enmId) => enms.find((e) => e.id === enmId)?.name)
                        .filter(Boolean)
                        .join(', ')
                    : 'Not selected'}
                </div>
                <div>
                  <strong>Auto-Onboarding:</strong> {formData.autoOnboarding ? 'On' : 'Off'} (
                  {formData.categoryTools.onboarding
                    .map((t) =>
                      t.toolId === 'rloRapp'
                        ? 'RLO rApp'
                        : t.toolId === 'rlca'
                          ? 'RLCA Healthcheck'
                          : repositories.find((r) => r.id === t.toolId)?.name || t.toolId
                    )
                    .join(', ') || 'Not selected'}
                  )
                </div>
                {showDeploymentStep && (
                  <div>
                    <strong>Auto-Deploy:</strong> {formData.deploymentAutomation ? 'On' : 'Off'} (
                    {formData.categoryTools.deployment
                      .map((t) =>
                        t.toolId === 'rhc'
                          ? 'RHC Healthcheck'
                          : t.toolId === 'rloRapp'
                            ? 'RLO rApp'
                            : t.toolId === 'rlca'
                              ? 'RLCA Healthcheck'
                              : moshells.find((m) => m.id === t.toolId)?.name ||
                                repositories.find((r) => r.id === t.toolId)?.name ||
                                t.toolId
                      )
                      .join(', ') || 'Not selected'}
                    )
                  </div>
                )}
                {showKpiStep && (
                  <div>
                    <strong>KPI Evaluation:</strong>{' '}
                    {formData.categoryTools.kpiEvaluation
                      .map((t) => repositories.find((r) => r.id === t.toolId)?.name || t.toolId)
                      .join(', ') || 'Not selected'}
                  </div>
                )}
                {showTestingStep && (
                  <div>
                    <strong>Auto-Test:</strong> {formData.testAutomation ? 'On' : 'Off'}
                  </div>
                )}
              </div>
            </ReactEDSWrapper>
          </ReactEDSWrapper>
        </ReactEDSWrapper>
      </ReactEDSWrapper>
    </div>
  );
}
